import _ from 'lodash';
import React, {FC, ReactNode, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownPlacement} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownCategory} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {
  AsyncSearchDropdown,
  AsyncSearchDropdownDataFetcher,
} from '../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {SearchDropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {
  Skeleton,
  SkeletonColorSchemesEnum,
} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {addNewAccountAccountId} from '../../../../helpers/models/cardAttributeUpdateHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {
  fetchAccountsByQueryAsyncAction,
  FetchAccountsByQueryOptions,
} from '../../../../store/data/accounts/accountsActions';
import {AccountAvatar} from '../../avatars/accountAvatar';
import {DropdownItemAccentIcon} from '../dropdownItemAccentIcon';
import {TypedRowValue} from '../source/dropdownSource';

/*
 * Constants.
 */

const dropdownSmallRowHeight = 30;
const dropdownLargeRowHeight = 48;
const dropdownHeight = 342;
const accountsFetchLimit = 20;

enum SearchAccountDropdownItemsEnum {
  CATEGORY = 'CATEGORY',
  NEW_ACTION = 'NEW_ACTION',
  ACCOUNT = 'ACCOUNT',
  NONE = 'NONE',
}

/*
 * Props.
 */

interface SearchAccountsDropdownProps
  extends Pick<SearchDropdownProps<SearchAccountsDropdownRowTypedValue>, 'headerRightIcon'> {
  popoverProps: PopoverTransferProps;
  searchPlaceholder?: string;
  selectedAccountId?: number;
  placement?: DropdownPlacement;

  onConfirm: (model: AccountModel) => void;
  onDelete?: () => void;
  onNewAccountClick?: (arg: string) => void;
}

type SearchAccountsDropdownRowTypedValue =
  | TypedRowValue<SearchAccountDropdownItemsEnum.CATEGORY, ReactNode>
  | TypedRowValue<SearchAccountDropdownItemsEnum.NEW_ACTION, ReactNode>
  | TypedRowValue<SearchAccountDropdownItemsEnum.ACCOUNT, AccountModel>
  | TypedRowValue<SearchAccountDropdownItemsEnum.NONE, undefined>;

interface SearchAccountsPaginationData {
  pageToken?: string;
}

/*
 * Style.
 */

const StyledDropdownEmpty = styled(DropdownEmpty)`
  padding-top: 32px;
  text-align: center;
  overflow: hidden;
`;

const StyledDropdownItem = styled(DropdownItem)`
  display: flex;
  flex-flow: row;
  height: ${dropdownLargeRowHeight}px;
  padding-right: 16px;
`;

const StyledDropdownAccountContainerDiv = styled.div`
  margin: 0 12px;
  align-self: center;
`;

const StyledAccountInformationContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

const StyledDropdownSpacerDiv = styled.div`
  margin-left: 12px;
`;

const StyledAccountNameDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.medium};
  ${ellipsis()};
`;

const StyledSearchAccountLoadingContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  height: 48px;
`;

const StyledAccountLoadingDiv = styled(StyledDropdownAccountContainerDiv)`
  padding-top: 4px;
  padding-left: 12px;
  margin: 0;
  flex: 0 0 44px;
`;

const StyledLineSkeleton = styled(Skeleton)`
  margin-top: 4px;
`;

const StyledAccountLoadingDetailsDiv = styled.div`
  flex: 1;
  padding-right: 12px;
`;

const StyledAccountDetailsContainerDiv = styled.div`
  display: flex;
  flex-flow: column;
  vertical-align: middle;
`;

const StyledTooltipOverflowCondition = styled(TooltipOverflowCondition)`
  direction: ltr;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  margin-top: 4px;
`;

const StyledLoadingRendererContainerDiv = styled.div`
  padding: 7px 0;
`;

const StyledDropdownItemAccentIcon = styled(DropdownItemAccentIcon)`
  padding: 4px;
`;

const StyledDropdownCategory = styled(DropdownCategory)`
  &:not(:first-child) {
    margin-top: 2px;
  }
`;

const StyledDropdownItemContent = styled(DropdownItemContent)`
  display: flex;
  align-items: center;
`;

const StyledCreateSearchedAccountContainerDiv = styled.div`
  display: flex;
  margin-top: 8px;
`;

const StyledCreateButton = styled(Button)`
  overflow: hidden;
`;

const StyledButtonContentDiv = styled.div`
  ${ellipsis()}
`;

/*
 * Component.
 */

export const SearchAccountsDropdown: FC<SearchAccountsDropdownProps> = (props) => {
  const {
    popoverProps,
    searchPlaceholder,
    placement,
    selectedAccountId,
    onConfirm,
    onDelete,
    onNewAccountClick,
    headerRightIcon,
  } = props;
  const shouldDisplayCreateAction = Boolean(onNewAccountClick);
  const shouldDisplayDeleteAction = Boolean(onDelete);
  const itemsAndCategories = useMemo(
    () => buildItemsAndCategories(shouldDisplayCreateAction, shouldDisplayDeleteAction),
    [shouldDisplayCreateAction, shouldDisplayDeleteAction],
  );
  const [query, setQuery] = useState('');

  const dispatch = useAppDispatch();

  const onFetchAccountsByQueryAsync = useCallback(
    async (options: FetchAccountsByQueryOptions) => dispatch(fetchAccountsByQueryAsyncAction(options)),
    [dispatch],
  );

  const onCreateClick = useCallback(() => {
    if (onNewAccountClick) {
      onNewAccountClick(query || '');
    }
  }, [onNewAccountClick, query]);

  const fetcher: AsyncSearchDropdownDataFetcher<
    SearchAccountsDropdownRowTypedValue,
    SearchAccountsPaginationData
  > = useCallback(
    async (_searchQuery, items, cancelToken, paginationData) => {
      const response = await onFetchAccountsByQueryAsync({
        query,
        limit: accountsFetchLimit,
        cancelToken,
        pageToken: paginationData?.pageToken,
      });
      cancelToken.throwIfRequested();

      const newAccountDropdownItems = response.accounts.map(buildDropdownAccount);

      return {
        data: buildDropdownItems(query, items, newAccountDropdownItems, itemsAndCategories),
        canRequestMore: Boolean(response.pageToken),
        paginationData: {pageToken: response.pageToken},
      };
    },
    [itemsAndCategories, onFetchAccountsByQueryAsync, query],
  );

  return (
    <AsyncSearchDropdown
      {...popoverProps}
      headerTitle={<Trans id="search-accounts-dropdown-title">Accounts</Trans>}
      headerRightIcon={headerRightIcon}
      searchInputPlaceholder={searchPlaceholder}
      query={query}
      onQueryChange={setQuery}
      height={dropdownHeight}
      placement={placement}
      searchInputShouldIncludeSearchIcon
      onRequestData={fetcher}
      renderRow={(item) => {
        if (item.type === SearchAccountDropdownItemsEnum.CATEGORY) {
          return renderCategory(item.value);
        }
        if (item.type === SearchAccountDropdownItemsEnum.NEW_ACTION) {
          return renderCreateRow(onCreateClick);
        }
        if (item.type === SearchAccountDropdownItemsEnum.NONE) {
          return renderNone(selectedAccountId, onDelete);
        }
        return renderAccount(item.value, selectedAccountId, onConfirm);
      }}
      renderEmpty={() => renderEmptyState(query, shouldDisplayCreateAction, onCreateClick)}
      findRowHeight={(item) => {
        switch (item?.type) {
          case SearchAccountDropdownItemsEnum.CATEGORY:
          case SearchAccountDropdownItemsEnum.NEW_ACTION:
            return dropdownSmallRowHeight;
          case SearchAccountDropdownItemsEnum.NONE:
          case SearchAccountDropdownItemsEnum.ACCOUNT:
          default:
            return dropdownLargeRowHeight;
        }
      }}
      renderLoading={() => (
        <StyledLoadingRendererContainerDiv>
          {renderAccountLoadingRow()}
          {renderAccountLoadingRow()}
          {renderAccountLoadingRow()}
          {renderAccountLoadingRow()}
          {renderAccountLoadingRow()}
        </StyledLoadingRendererContainerDiv>
      )}
    />
  );
};

/*
 * Render helpers.
 */

function buildDropdownItems(
  query: string,
  existingItems: ReadonlyArray<SearchAccountsDropdownRowTypedValue>,
  newItems: ReadonlyArray<SearchAccountsDropdownRowTypedValue>,
  initialItems: ReadonlyArray<SearchAccountsDropdownRowTypedValue>,
) {
  if (existingItems.length === 0 && !query) {
    return [...initialItems, ...newItems];
  }
  return [...existingItems, ...newItems];
}

function renderAccount(
  account: AccountModel | undefined,
  selectedAccountId: number | undefined,
  onConfirm: (model: AccountModel) => void,
) {
  if (!account) {
    return null;
  }
  const domainsJoined = account.domains.join(', ');
  return (
    <StyledDropdownItem
      key={account.id}
      onSelect={() => {
        onConfirm(account);
      }}
      isChecked={selectedAccountId === account.id}
      shouldReserveRightIcon
    >
      <StyledDropdownAccountContainerDiv>
        <AccountAvatar account={account} avatarIconSize={12} size={VisualSizesEnum.MEDIUM} />
      </StyledDropdownAccountContainerDiv>
      <StyledAccountInformationContainerDiv>
        <TooltipCoordinator render={() => domainsJoined} placement="bottom">
          <StyledAccountDetailsContainerDiv>
            <StyledAccountNameDiv>{account.name}</StyledAccountNameDiv>
            {account.domains.length > 0 && (
              <StyledTooltipOverflowCondition>{domainsJoined}</StyledTooltipOverflowCondition>
            )}
          </StyledAccountDetailsContainerDiv>
        </TooltipCoordinator>
      </StyledAccountInformationContainerDiv>
      <StyledDropdownSpacerDiv />
    </StyledDropdownItem>
  );
}

function renderCreateRow(onCreateClick: () => void) {
  return (
    <DropdownItem
      interactionId="search-accounts-dropdown-new-account"
      onSelect={(event) => {
        onCreateClick();
      }}
    >
      <StyledDropdownItemAccentIcon label={null} iconName="plusCircleFilledSmall" shouldHideBackground />
      <StyledDropdownItemContent>
        <Trans id="search-accounts-dropdown-new-account-action">New account</Trans>
      </StyledDropdownItemContent>
    </DropdownItem>
  );
}

function renderCategory(value: ReactNode) {
  return <StyledDropdownCategory>{value}</StyledDropdownCategory>;
}

function renderNone(selectedAccountId: number | undefined, onSelect: (() => void) | undefined) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledDropdownItem
          onSelect={onSelect}
          shouldReserveRightIcon
          isChecked={!selectedAccountId || selectedAccountId === addNewAccountAccountId}
        >
          <StyledDropdownAccountContainerDiv>
            <Icon name="none" color={theme.greys.shade50} />
          </StyledDropdownAccountContainerDiv>
          <StyledAccountInformationContainerDiv>
            <Trans id="search-account-dropdown-none">None</Trans>
          </StyledAccountInformationContainerDiv>
        </StyledDropdownItem>
      )}
    </UserThemeConsumer>
  );
}

function renderEmptyState(
  query: string,
  shouldDisplayCreateAction: boolean,
  onCreateClick: (arg: string) => void,
) {
  if (query) {
    return (
      <StyledDropdownEmpty maxWidth="100%">
        <Trans id="search-accounts-dropdown-no-results">No results</Trans>
        {shouldDisplayCreateAction && (
          <StyledCreateSearchedAccountContainerDiv>
            <StyledCreateButton variant={ButtonVariantsEnum.LINK} onClick={() => onCreateClick(query.trim())}>
              <StyledButtonContentDiv>
                <Trans id="search-accounts-dropdown-create-searched-account">
                  Create account &quot;{query}&quot;
                </Trans>
              </StyledButtonContentDiv>
            </StyledCreateButton>
          </StyledCreateSearchedAccountContainerDiv>
        )}
      </StyledDropdownEmpty>
    );
  }
  return (
    <StyledDropdownEmpty>
      <Trans id="search-accounts-dropdown-empty-placeholder">Type to search</Trans>
    </StyledDropdownEmpty>
  );
}

function renderAccountLoadingRow() {
  return (
    <StyledSearchAccountLoadingContainerDiv>
      <StyledAccountLoadingDiv>
        <Skeleton width="20px" height="20px" borderRadius="50%" />
      </StyledAccountLoadingDiv>
      <StyledAccountLoadingDetailsDiv>
        <StyledLineSkeleton height="16px" width="120px" />
        <StyledLineSkeleton height="16px" scheme={SkeletonColorSchemesEnum.GREY10} />
      </StyledAccountLoadingDetailsDiv>
    </StyledSearchAccountLoadingContainerDiv>
  );
}

function buildItemsAndCategories(
  shouldDisplayCreateAction: boolean,
  shouldDisplayDeleteAction: boolean,
): ReadonlyArray<SearchAccountsDropdownRowTypedValue> {
  const createHeaderAndAction = [
    buildDropdownCategory(<Trans id="search-accounts-dropdown-create-header">Create</Trans>),
    buildDropdownNewAccountAction(),
  ];
  const allAccountsHeader = buildDropdownCategory(
    <Trans id="search-accounts-dropdown-all-accounts-header">All accounts</Trans>,
  );

  return _.compact([
    ...(shouldDisplayCreateAction ? createHeaderAndAction : []),
    shouldDisplayCreateAction ? allAccountsHeader : undefined,
    shouldDisplayDeleteAction ? buildDropdownNone() : undefined,
  ]);
}

function buildDropdownCategory(value: ReactNode): SearchAccountsDropdownRowTypedValue {
  return {
    type: SearchAccountDropdownItemsEnum.CATEGORY,
    value,
  };
}

function buildDropdownNewAccountAction(): SearchAccountsDropdownRowTypedValue {
  return {
    type: SearchAccountDropdownItemsEnum.NEW_ACTION,
    value: undefined,
  };
}

function buildDropdownAccount(account: AccountModel): SearchAccountsDropdownRowTypedValue {
  return {
    type: SearchAccountDropdownItemsEnum.ACCOUNT,
    value: account,
  };
}

function buildDropdownNone(): SearchAccountsDropdownRowTypedValue {
  return {
    type: SearchAccountDropdownItemsEnum.NONE,
    value: undefined,
  };
}
